"use client";

import { c as _c } from "react/compiler-runtime";
import { HomepageFooter } from "@/components/homepage/HomepageFooter";
import { FilterSidebar } from "@/components/ui/filter-sidebar";
import { PageLayout } from "@/components/ui/page-layout";
import { PixelArrow } from "@/components/ui/pixel-arrow";
import { PixelLoader } from "@/components/ui/pixel-loader";
import { V2_ENDPOINT } from "@/consts";
import { EASE_OUT_QUART } from "@/lib/animation-constants";
import type { Template, TemplatesApiResponse } from "@/stores/templatesStore";
import { useIsAuthenticated } from "@/hooks/useIsAuthenticated";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { motion } from "motion/react";
import { usePathname, useRouter, useSearchParams } from "next/navigation";
import { Suspense, useEffect, useMemo, useRef, useState } from "react";
import { PublicTemplateCard } from "./PublicTemplateCard";
import { TemplateHero } from "./TemplateHero";
import { TemplateCategoryBar } from "./TemplateSidebar";
const ALL_TAGS_KEY = "__all__";
const PAGE_SIZE = 30;

/** Curated sidebar categories (alphabetical). Always shown regardless of template count. */
const SIDEBAR_CATEGORIES: {
  name: string;
  slug: string;
}[] = [{
  name: "Accounting",
  slug: "accounting"
}, {
  name: "Compliance",
  slug: "compliance"
}, {
  name: "DeFi",
  slug: "defi"
}, {
  name: "Developer",
  slug: "developer"
}, {
  name: "Hyperliquid",
  slug: "hyperliquid"
}, {
  name: "Onchain Ops",
  slug: "onchain-ops"
}, {
  name: "Payment",
  slug: "payment"
}, {
  name: "Polymarket",
  slug: "polymarket"
}, {
  name: "Privacy",
  slug: "privacy"
}, {
  name: "Trading",
  slug: "trading"
}, {
  name: "Treasury",
  slug: "treasury"
}, {
  name: "Wallet",
  slug: "wallet"
}];
async function fetchPublicTemplates(tags?: string[]): Promise<Template[]> {
  const allTemplates: Template[] = [];
  let offset = 0;
  const limit = 100;
  while (true) {
    const params = new URLSearchParams();
    params.append("limit", String(limit));
    params.append("offset", String(offset));
    params.append("category", "public");
    if (tags && tags.length > 0) {
      tags.forEach(tag => params.append("tags", tag));
    }
    const url = `${V2_ENDPOINT}/v1/templates?${params.toString()}`;
    const response = await fetch(url);
    if (!response.ok) {
      throw new Error(`Failed to fetch templates: ${response.statusText}`);
    }
    const data: TemplatesApiResponse = await response.json();
    if (data.code !== 0) {
      throw new Error(`API error: ${data.message}`);
    }
    allTemplates.push(...(data.data.items || []));
    if (!data.data.hasMore) break;
    offset += limit;
  }
  return allTemplates;
}
function TemplatesLibraryContent({
  initialTemplates
}: {
  initialTemplates?: Template[];
}) {
  const router = useRouter();
  const pathname = usePathname();
  const searchParams = useSearchParams();
  const isAuthenticated = useIsAuthenticated();
  const [selectedTagSlug, setSelectedTagSlug] = useState<string | null>(() => searchParams.get("category") || null);
  const [searchQuery, setSearchQuery] = useState("");
  const [visibleCount, setVisibleCount] = useState(PAGE_SIZE);
  const queryClient = useQueryClient();
  const {
    data,
    isFetching
  } = useQuery({
    queryKey: ["public-templates", selectedTagSlug ?? "all"],
    queryFn: () => fetchPublicTemplates(selectedTagSlug ? [selectedTagSlug] : undefined),
    initialData: selectedTagSlug === null ? initialTemplates : undefined,
    staleTime: 5 * 60 * 1000
  });
  const {
    data: allTemplatesForCounts
  } = useQuery({
    queryKey: ["public-templates", "all"],
    queryFn: () => fetchPublicTemplates(),
    initialData: initialTemplates,
    staleTime: 5 * 60 * 1000
  });
  const tagCountMap = useMemo(() => {
    const counts: Record<string, number> = {};
    for (const t of allTemplatesForCounts ?? []) {
      if (t.archived) continue;
      for (const tag of t.tags ?? []) {
        counts[tag.slug] = (counts[tag.slug] ?? 0) + 1;
      }
    }
    return counts;
  }, [allTemplatesForCounts]);

  // Cross-page cache invalidation: when a template is mutated on another page
  // (e.g. the detail page), the TanStack query cache is typically empty (GC'd).
  // The store writes a timestamp to sessionStorage; we check it on mount and
  // force a refetch if the data is stale.
  useEffect(() => {
    try {
      const invalidatedAt = Number(sessionStorage.getItem("b3os_templates_invalidated_at") || "0");
      if (invalidatedAt > 0) {
        // Clear the signal so we don't refetch on every subsequent mount.
        sessionStorage.removeItem("b3os_templates_invalidated_at");
        queryClient.invalidateQueries({
          queryKey: ["public-templates"]
        });
      }
    } catch {
      // sessionStorage unavailable — skip.
    }
  }, [queryClient]);
  // Defense-in-depth: filter archived templates that may appear in stale cached responses
  const templates = useMemo(() => (data ?? []).filter(t => !t.archived), [data]);
  const showLoading = isFetching && data === undefined;
  useEffect(() => {
    setVisibleCount(PAGE_SIZE);
  }, [selectedTagSlug]);
  useEffect(() => {
    const slugParam = searchParams.get("category") || null;
    setSelectedTagSlug(prev => prev === slugParam ? prev : slugParam);
  }, [searchParams]);
  const filteredTemplates = useMemo(() => {
    if (!searchQuery.trim()) return templates;
    const query = searchQuery.toLowerCase();
    return templates.filter(t => t.name.toLowerCase().includes(query) || t.description?.toLowerCase().includes(query));
  }, [templates, searchQuery]);
  const featuredTemplates = useMemo(() => templates.filter(t => t.isPromoted), [templates]);
  const featuredScrollRef = useRef<HTMLDivElement>(null);
  const handleFeaturedScroll = (direction: "prev" | "next") => {
    const el = featuredScrollRef.current;
    if (!el) return;
    const delta = direction === "next" ? el.clientWidth : -el.clientWidth;
    el.scrollBy({
      left: delta,
      behavior: "smooth"
    });
  };
  const visibleTemplates = filteredTemplates.slice(0, visibleCount);
  const remainingCount = filteredTemplates.length - visibleCount;
  const hasMore = remainingCount > 0;
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const loadMoreRef = useRef<HTMLButtonElement | null>(null);
  const handleLoadMore = () => {
    if (isLoadingMore) return;
    setIsLoadingMore(true);
    // Brief tick so the pixel loader is visible — the slice itself is sync.
    requestAnimationFrame(() => {
      setVisibleCount(prev => prev + PAGE_SIZE);
      setIsLoadingMore(false);
    });
  };

  // Auto-trigger load when the "Load more" button scrolls near the viewport.
  useEffect(() => {
    const el = loadMoreRef.current;
    if (!el || !hasMore) return;
    const observer = new IntersectionObserver(entries => {
      if (entries[0]?.isIntersecting) handleLoadMore();
    }, {
      rootMargin: "200px"
    });
    observer.observe(el);
    return () => observer.disconnect();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasMore, visibleCount]);
  const handleTagChange = (slug: string | null) => {
    setSelectedTagSlug(slug);
    const params = new URLSearchParams(searchParams.toString());
    if (slug === null) {
      params.delete("category");
    } else {
      params.set("category", slug);
    }
    const queryString = params.toString();
    router.replace(queryString ? `${pathname}?${queryString}` : pathname, {
      scroll: false
    });
  };
  const featuredSection = featuredTemplates.length > 0 && <section className="w-full">
      <motion.div initial={{
      opacity: 0,
      y: 12
    }} animate={{
      opacity: 1,
      y: 0
    }} transition={{
      duration: 0.5,
      ease: EASE_OUT_QUART
    }} className="mb-4 flex items-center justify-between gap-4">
        <h2 className="text-ds-text-primary leading-30 text-xl font-medium">Featured templates</h2>
        {featuredTemplates.length > 2 && <div className="flex items-start pr-px">
            {([{
          dir: "prev",
          label: "Previous",
          arrow: "left"
        }, {
          dir: "next",
          label: "Next",
          arrow: "right"
        }] as const).map(btn => <button key={btn.dir} type="button" aria-label={btn.label} onClick={() => handleFeaturedScroll(btn.dir)} className="hover:bg-ds-surface-hover border-ds-border bg-ds-surface-card group -mr-px flex size-9 cursor-pointer items-center justify-center overflow-hidden border p-[3px] transition-colors">
                <PixelArrow direction={btn.arrow} />
              </button>)}
          </div>}
      </motion.div>
      <div ref={featuredScrollRef} className="scrollbar-hide flex snap-x snap-mandatory gap-5 overflow-x-auto scroll-smooth">
        {featuredTemplates.map((template, idx) => <motion.div key={template.id} initial={{
        opacity: 0,
        x: 24
      }} animate={{
        opacity: 1,
        x: 0
      }} transition={{
        duration: 0.5,
        delay: 0.1 + idx * 0.08,
        ease: EASE_OUT_QUART
      }} className="w-full shrink-0 snap-start md:w-[calc(50%-10px)]">
            <PublicTemplateCard template={template} variant="featured" />
          </motion.div>)}
      </div>
    </section>;
  const mainContent = <div className="flex w-full gap-6">
      {/* Sidebar */}
      <FilterSidebar groups={[{
      kind: "select",
      items: [{
        key: ALL_TAGS_KEY,
        label: "All Use Cases"
      }, ...SIDEBAR_CATEGORIES.map(t => ({
        key: t.slug,
        label: t.name,
        count: tagCountMap[t.slug] || undefined
      }))],
      activeKey: selectedTagSlug ?? ALL_TAGS_KEY,
      onChange: k => handleTagChange(k === ALL_TAGS_KEY ? null : k)
    }]} />

      {/* Template Grid */}
      <div className="min-w-0 flex-1">
        <TemplateCategoryBar selectedTagSlug={selectedTagSlug} onTagChange={handleTagChange} tags={SIDEBAR_CATEGORIES} />

        <div className="mb-4 flex flex-col gap-3 md:flex-row md:items-center md:gap-5">
          <h2 className="text-ds-text-primary leading-30 flex-1 text-xl font-medium">All templates</h2>
          <div className="rounded-ds-md border-ds-border bg-ds-surface-sunken shadow-ds-edge-light-debossed relative flex h-11 w-full items-center gap-2 border py-[10px] pl-[10px] pr-2 md:w-[400px]">
            <img src="/templates/search.svg" alt="" aria-hidden="true" className="size-5 shrink-0" />
            <input type="text" placeholder="Search templates..." value={searchQuery} onChange={e => setSearchQuery(e.target.value)} className="text-content-secondary placeholder:text-ds-text-disabled leading-label flex-1 bg-transparent text-sm outline-none" />
          </div>
        </div>

        {showLoading ? <div className="flex items-center justify-center py-20">
            <PixelLoader size="xl" color="blue" />
          </div> : filteredTemplates.length === 0 ? <div className="flex flex-col items-center justify-center py-20 text-center">
            <p className="text-ds-text-muted text-base">
              {searchQuery ? "No templates match your search" : "No templates found"}
            </p>
            {searchQuery && <button type="button" onClick={() => setSearchQuery("")} className="text-ds-focus-brand mt-2 text-sm hover:underline">
                Clear search
              </button>}
          </div> : <div className="flex flex-col items-center gap-8">
            <div className="grid w-full grid-cols-1 gap-8 sm:gap-5 md:grid-cols-2 lg:grid-cols-3">
              {visibleTemplates.map((template, idx) => <motion.div key={template.id} initial={{
            opacity: 0,
            y: 16
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 0.4,
            delay: Math.min(idx % 6, 5) * 0.06,
            ease: EASE_OUT_QUART
          }}>
                  <PublicTemplateCard template={template} />
                </motion.div>)}
            </div>

            {/* Load More — auto-fires via IntersectionObserver; click is a fallback. */}
            {hasMore && <button ref={loadMoreRef} type="button" onClick={handleLoadMore} disabled={isLoadingMore} className="hover:bg-ds-surface-hover border-ds-border bg-ds-surface-card text-ds-text-subtle shadow-ds-soft inline-flex items-center gap-2 rounded-lg border px-6 py-2.5 text-sm font-medium transition-colors disabled:opacity-70">
                {isLoadingMore && <PixelLoader size="xs" />}
                {isLoadingMore ? "Loading..." : `Load more (${remainingCount})`}
              </button>}
          </div>}
      </div>
    </div>;
  if (isAuthenticated) {
    return <PageLayout>
        <div className="flex flex-col gap-10">
          <TemplateHero flush />
          {featuredSection}
          {mainContent}
        </div>
      </PageLayout>;
  }
  return <div className="bg-background flex min-h-screen flex-col pt-[72px]">
      <TemplateHero />

      {featuredTemplates.length > 0 && <div className="mx-auto w-full max-w-[1300px] px-4 pt-10">{featuredSection}</div>}

      <div className="mx-auto w-full max-w-[1300px] gap-6 px-4 pb-16 pt-10">{mainContent}</div>

      <HomepageFooter hideGrids />
    </div>;
}
export default function TemplatesPageClient(t0) {
  const $ = _c(2);
  const {
    initialTemplates
  } = t0;
  let t1;
  if ($[0] !== initialTemplates) {
    t1 = <Suspense fallback={null}><TemplatesLibraryContent initialTemplates={initialTemplates} /></Suspense>;
    $[0] = initialTemplates;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  return t1;
}